import { type MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import type { CardVerificationCodeElement } from '@basis-theory/basis-theory-js/types/elements';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import { setError } from '@/actions';
import { cardPaymentAttempt } from '@/actions/payment';
import errorMonitoring from '@/errorMonitoring';
import basisTheoryCheckoutEngine from '@/payments/core/engines/basisTheory';
import type { BasisTheoryClient } from 'services/basisTheory';
import { getInstallmentCardFamily, type InstallmentPlan } from 'services/installments';
import type { StoredCard } from 'services/storedCards';
import { buildBrowserInfo } from './basisTheorySubmission';

type Dispatch = (action: unknown) => unknown;

interface StoredCardPaymentValues {
  basisTheoryTokenId?: string;
  cardBrand: string;
  cvvTokenIntentId?: string;
  holderName: string;
  monthlySelectedPlan?: number;
  saveCard: boolean;
  vaultCardId?: string;
}

interface StoredCardSubmitState {
  purchase: { get: (key: string) => unknown };
  whitelabelConfig: { env: { currency: string } };
}

interface UseStoredCardSubmitOptions {
  active: boolean;
  clientRef: MutableRefObject<BasisTheoryClient | null>;
  cvvComplete: boolean;
  cvvElementRef: MutableRefObject<CardVerificationCodeElement | null>;
  cvvOpen: boolean;
  onRequireCvv: () => void;
  selectedCard?: StoredCard;
  selectedInstallmentPlan?: InstallmentPlan;
  sessionKey: string;
}

function requiresCvv(card: StoredCard): boolean {
  return card.paymentMode.kind === 'tokenized_with_cvv';
}

function validatedInstallmentMonths(
  card: StoredCard,
  selectedPlan?: InstallmentPlan,
): number | undefined {
  if (!card.installmentsAvailable || !selectedPlan || selectedPlan.months <= 1) return undefined;
  return selectedPlan.card === getInstallmentCardFamily(card.brand)
    ? selectedPlan.months
    : undefined;
}

/** Register and execute the payment owned by the selected stored card. */
function useStoredCardSubmit({
  active,
  clientRef,
  cvvComplete,
  cvvElementRef,
  cvvOpen,
  onRequireCvv,
  selectedCard,
  selectedInstallmentPlan,
  sessionKey,
}: UseStoredCardSubmitOptions): { isSubmitting: boolean; submit: () => Promise<boolean> } {
  const dispatch = useDispatch() as Dispatch;
  const currency = useSelector(
    (state: StoredCardSubmitState) => state.whitelabelConfig.env.currency,
  );
  const purchaseToken = useSelector(
    (state: StoredCardSubmitState) => state.purchase.get('token') as string,
  );
  const mountedRef = useRef(true);
  const activeRef = useRef(active);
  activeRef.current = active;
  const cvvOpenRef = useRef(cvvOpen);
  cvvOpenRef.current = cvvOpen;
  const selectedCardRef = useRef(selectedCard);
  selectedCardRef.current = selectedCard;
  const sessionKeyRef = useRef(sessionKey);
  sessionKeyRef.current = sessionKey;
  const ownershipRevisionRef = useRef(0);
  const activeAttemptRef = useRef<object>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  useEffect(() => {
    ownershipRevisionRef.current += 1;
  }, [active, cvvOpen, selectedCard?.id, sessionKey]);

  const submit = useCallback(async () => {
    if (!active || !selectedCard || activeAttemptRef.current) return false;
    if (selectedCard.isExpired || selectedCard.paymentMode.kind === 'unavailable') return false;

    const shouldTokenizeCvv = requiresCvv(selectedCard);
    if (shouldTokenizeCvv && !cvvComplete) {
      onRequireCvv();
      return false;
    }

    const submissionAttemptId = uuidv4();
    const submissionAttempt = {};
    const submissionOwnershipRevision = ownershipRevisionRef.current;
    const submissionSessionKey = sessionKey;
    const submissionStartedWithCvvOpen = cvvOpen;
    function submissionIsCurrent() {
      return (
        mountedRef.current &&
        activeAttemptRef.current === submissionAttempt &&
        ownershipRevisionRef.current === submissionOwnershipRevision &&
        activeRef.current &&
        selectedCardRef.current?.id === selectedCard.id &&
        sessionKeyRef.current === submissionSessionKey &&
        (!shouldTokenizeCvv || (submissionStartedWithCvvOpen && cvvOpenRef.current))
      );
    }
    activeAttemptRef.current = submissionAttempt;
    setIsSubmitting(true);

    try {
      let cvvTokenIntentId: string | undefined;
      if (shouldTokenizeCvv) {
        const client = clientRef.current;
        const cvvElement = cvvElementRef.current;
        if (!client || !cvvElement) throw new Error('stored_card_cvv_unavailable');
        const tokenIntent = await client.tokenIntents.create(
          {
            type: 'token',
            data: { cvc: cvvElement },
          },
          { correlationId: submissionAttemptId, idempotencyKey: submissionAttemptId },
        );
        if (!tokenIntent?.id) throw new Error('stored_card_cvv_token_intent_missing');
        cvvTokenIntentId = tokenIntent.id;
      }

      const paymentMode = selectedCard.paymentMode;
      const monthlySelectedPlan = validatedInstallmentMonths(selectedCard, selectedInstallmentPlan);
      const cardValues: StoredCardPaymentValues = {
        cardBrand: selectedCard.brand,
        holderName: selectedCard.holderName,
        ...(monthlySelectedPlan ? { monthlySelectedPlan } : {}),
        saveCard: paymentMode.kind !== 'vaulted',
        ...(paymentMode.kind === 'vaulted'
          ? { vaultCardId: paymentMode.vaultCardId }
          : { basisTheoryTokenId: paymentMode.basisTheoryTokenId }),
        ...(cvvTokenIntentId ? { cvvTokenIntentId } : {}),
      };

      if (submissionIsCurrent()) {
        await Promise.resolve(
          dispatch(
            cardPaymentAttempt(purchaseToken, cardValues, {
              browserInfo: buildBrowserInfo(),
              cardCheckoutMode: 'basis_theory',
              currency,
            }),
          ),
        );
      }
      return submissionIsCurrent();
    } catch (error) {
      errorMonitoring.notify({
        error,
        info: { correlationId: submissionAttemptId, stage: 'stored_card_payment' },
      });
      if (submissionIsCurrent()) {
        dispatch(
          setError(
            200,
            'check_the_fields_in_red',
            'warning',
            false,
            undefined,
            undefined,
            undefined,
            'global',
            undefined,
          ),
        );
      }
      return false;
    } finally {
      if (activeAttemptRef.current === submissionAttempt) {
        activeAttemptRef.current = undefined;
        if (mountedRef.current) setIsSubmitting(false);
      }
    }
  }, [
    active,
    clientRef,
    currency,
    cvvComplete,
    cvvElementRef,
    cvvOpen,
    dispatch,
    onRequireCvv,
    purchaseToken,
    selectedCard,
    selectedInstallmentPlan,
    sessionKey,
  ]);

  const submitRef = useRef(submit);
  submitRef.current = submit;
  const registrationRef = useRef<
    ReturnType<typeof basisTheoryCheckoutEngine.registerSubmitHandler> | undefined
  >();

  useEffect(() => {
    if (!active) return undefined;
    const registration = basisTheoryCheckoutEngine.registerSubmitHandler(
      () => submitRef.current(),
      'ready',
    );
    registrationRef.current = registration;
    return () => {
      registration.unregister();
      registrationRef.current = undefined;
    };
  }, [active]);

  useEffect(() => {
    if (active) registrationRef.current?.updateStatus(isSubmitting ? 'submitting' : 'ready');
  }, [active, isSubmitting]);

  return { isSubmitting, submit };
}

export { useStoredCardSubmit };
